import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { User, LogOut, Shield, LayoutList, ScrollText, ClipboardList, BookOpen, Settings } from "lucide-react";
import { getModerationPendingCount } from "../lib/api";
import HelpModal from "./HelpModal";

import urbsexBandeau from "../assets/brand/urbsex-bandeau.png";

export default function AppHeader({ user, isAuthenticated, isAdmin, isModerator, logout, onLoginClick, onAccountClick, onCruisingClick }) {
  const [pendingCount, setPendingCount] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!isAdmin && !isModerator) return;
    getModerationPendingCount()
      .then(data => setPendingCount(data.count))
      .catch(() => {});
  }, [isAdmin, isModerator]);

  return (
    <>
    <div className="absolute top-0 left-0 right-0 z-50 bg-[#1d191a] border-b border-white/10 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img
          src={urbsexBandeau}
          alt="UrbSex"
          className="h-8 w-auto select-none"
          draggable="false"
        />

        <button
          onClick={() => setShowHelp(true)}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-semibold tracking-wide hover:bg-amber-500/30 transition-colors cursor-pointer"
        >
          <BookOpen className="w-3 h-3" />
          Mode d'emploi
        </button>

        {(isAdmin || isModerator) && pendingCount > 0 && (
          <button
            onClick={() => window.location.assign("/moderation")}
            className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] font-semibold tracking-wide hover:bg-red-500/30 transition-colors"
          >
            <ClipboardList className="w-3 h-3" />
            {pendingCount}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
                data-testid="user-menu-button"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{user?.pseudo}</span>
                {isAdmin && <Badge variant="secondary" className="text-xs">Admin</Badge>}
                {isModerator && <Badge variant="secondary" className="text-xs">Modérateur</Badge>}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              className="bg-zinc-950 text-white border border-white/10 shadow-xl"
            >
              <DropdownMenuItem disabled className="text-xs text-slate-500">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={onCruisingClick} className="cursor-pointer focus:bg-white/10 focus:text-white">
                <LayoutList className="w-4 h-4 mr-2" />
                Mon cruising
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onAccountClick} className="cursor-pointer focus:bg-white/10 focus:text-white">
                <Settings className="w-4 h-4 mr-2" />
                Mon compte
              </DropdownMenuItem>

              {isAdmin && (
                <DropdownMenuItem onClick={() => { window.location.assign("/admin"); }} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <Shield className="w-4 h-4 mr-2" />
                  Administration
                </DropdownMenuItem>
              )}
              {(isAdmin || isModerator) && (
                <DropdownMenuItem onClick={() => { window.location.assign("/moderation"); }} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Modération
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => window.location.assign("/legal")}
                className="cursor-pointer focus:bg-white/5 focus:text-white/40 text-white/40 text-xs"
              >
                <ScrollText className="w-3.5 h-3.5 mr-2 opacity-50" />
                Infos légales
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            data-testid="login-button"
            variant="outline"
            size="sm"
            onClick={onLoginClick}
            className="gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <User className="w-4 h-4" />
            Connexion
          </Button>
        )}
      </div>
    </div>

    {(isAdmin || isModerator) && pendingCount > 0 && (
      <div className="absolute top-12 left-0 right-0 z-40 sm:hidden bg-[#1d191a] border-b border-white/10 px-4 py-1 flex items-center justify-center">
        <button
          onClick={() => window.location.assign("/moderation")}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] font-semibold tracking-wide hover:bg-red-500/30 transition-colors"
        >
          <ClipboardList className="w-3 h-3" />
          {pendingCount} signalement{pendingCount > 1 ? "s" : ""} en attente
        </button>
      </div>
    )}

    <HelpModal open={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
}
